import { useCallback, useEffect, useRef, useState } from "react"
import useApi from "./useApi.js"

const POLL_MS = 3000
const ACTIVE_CRAWL = new Set([ "crawling" ])
const ACTIVE_SHOP = new Set([ "pending", "crawled", "onboarding" ])

// Fetches GET /api/v1/shop (profile embedded) and re-polls every 3s while a
// background job is in flight. Polling stops once the shop is active or the
// crawl fails. One request per poll (profile is embedded in the shop payload).
export default function useShop() {
  const api = useApi()
  const [state, setState] = useState({ status: "loading", shop: null, profile: null, error: null })
  const timerRef = useRef(null)
  const mountedRef = useRef(true)

  const fetchOnce = useCallback(async () => {
    try {
      const shopPayload = await api.get("/api/v1/shop")
      if (!mountedRef.current) return
      const { profile, ...shop } = shopPayload
      setState({ status: "loaded", shop, profile: profile || null, error: null })
    } catch (err) {
      if (!mountedRef.current) return
      if (/^404/.test(err.message)) {
        setState({ status: "loaded", shop: null, profile: null, error: null })
      } else {
        setState((prev) => ({ ...prev, status: "error", error: err.message }))
      }
    }
  }, [ api ])

  useEffect(() => {
    mountedRef.current = true
    fetchOnce()
    return () => { mountedRef.current = false }
  }, [ fetchOnce ])

  useEffect(() => {
    const shop = state.shop
    const jobInFlight =
      shop && ACTIVE_SHOP.has(shop.status) &&
      state.profile && ACTIVE_CRAWL.has(state.profile.crawl_status)
    const awaitingInstall =
      shop && shop.status === "active" && !shop.script_installed
    const shouldPoll = jobInFlight || awaitingInstall

    if (!shouldPoll) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
      return
    }

    if (timerRef.current) return
    timerRef.current = setInterval(fetchOnce, POLL_MS)
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    }
  }, [ state.shop, state.profile, fetchOnce ])

  return { ...state, refresh: fetchOnce }
}
